import * as Yup from 'yup';
import {
	SUB_BILLING_PER_ORDER,
	PER_ORDER_MIN_ROWS,
	PER_ORDER_MAX_ROWS,
} from './PerOrderRatesField';

/**
 * Extract filename from S3 key by removing UUID prefix
 * S3 keys are in format: {uuid}-{filename}
 * Example: "340d1b25-c372-439e-b92c-5306e4748a3-Data_Analytics_Assignment.pdf"
 * Returns: "Data_Analytics_Assignment.pdf"
 */
export const extractFilenameFromS3Key = (s3Key) => {
	if (!s3Key) return '';
	// UUID format: 8-4-4-4-12 characters (36 chars total + 1 hyphen)
	// Find the position after the UUID prefix (first 37 characters)
	const uuidPattern = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}-/i;
	const match = s3Key.match(uuidPattern);
	if (match) {
		return s3Key.substring(match[0].length);
	}
	// If no UUID pattern found, return the original key
	return s3Key;
};

// Billing Type Constants
export const BILLING_TYPE_FIXED_VARIABLE = 'Fixed + Variable';
export const BILLING_TYPE_FIXED = 'Fixed';
export const BILLING_TYPE_VARIABLE = 'Variable';
export const BILLING_TYPE_FIXED_PER_DAY = 'Fixed Per Day';

// Week Off Days
export const WEEK_OFF_DAYS = [
	'Sunday',
	'Monday',
	'Tuesday',
	'Wednesday',
	'Thursday',
	'Friday',
	'Saturday',
];

// Options Arrays
export const temperatureOptions = [
	{ value: true, label: 'Yes' },
	{ value: false, label: 'No' },
];

export const billingTypeOptions = [
	{ value: BILLING_TYPE_VARIABLE, label: 'Variable' },
	{ value: BILLING_TYPE_FIXED_VARIABLE, label: 'Fixed + Variable' },
	{ value: BILLING_TYPE_FIXED_PER_DAY, label: 'Fixed Rate Per Day' },
];

export const multipleTripsOptions = [
	{ value: true, label: 'Yes' },
	{ value: false, label: 'No' },
];

export const weekOffDaysOptions = WEEK_OFF_DAYS.map((day) => ({
	value: day,
	label: day,
}));

// Validation Schema based on API contract
export const FormSchema = Yup.object({
	vendor_id: Yup.mixed()
		.test('vendor-id-required', 'Vendor is required', (value) => {
			if (value === undefined || value === null) return false;
			if (typeof value === 'string') return value.trim() !== '';
			return true;
		})
		.label('Vendor'),
	project_id: Yup.number()
		.required('Project is required')
		.integer('Project ID must be an integer')
		.label('Project'),
	billing_type: Yup.string()
		.required('Billing type is required')
		.oneOf(
			[
				BILLING_TYPE_FIXED,
				BILLING_TYPE_VARIABLE,
				BILLING_TYPE_FIXED_VARIABLE,
				BILLING_TYPE_FIXED_PER_DAY,
			],
			'Invalid billing type'
		)
		.label('Billing Type'),
	temperature_controlled: Yup.boolean()
		.required('Temperature controlled is required')
		.label('Temperature Controlled'),
	vehicle_type_id: Yup.number()
		.required('Vehicle type is required')
		.integer('Vehicle type ID must be an integer')
		.label('Vehicle Type'),
	capacity_tonnes: Yup.number()
		.required('Capacity is required')
		.min(0.01, 'Capacity must be at least 0.01')
		.max(999.99, 'Capacity must not exceed 999.99')
		.label('Capacity'),
	contract_date: Yup.date().required('Contract date is required').label('Contract Date'),
	expiry_date: Yup.date()
		.required('Expiry date is required')
		// eslint-disable-next-line func-names
		.test('is-greater', 'Expiry date must be greater than contract date', function (value) {
			const { contract_date } = this.parent;
			if (!value || !contract_date) return true;
			return new Date(value) > new Date(contract_date);
		})
		.label('Expiry Date'),
	vehicle_ids: Yup.array()
		.of(Yup.mixed())
		.min(1, 'Please select at least one vehicle')
		.required('Please select at least one vehicle')
		.label('Vehicles'),
	selected_no_of_vehicles: Yup.number()
		.nullable()
		.integer('Number of vehicles must be an integer')
		.min(1, 'Number of vehicles must be at least 1')
		.max(999, 'Number of vehicles must not exceed 999')
		.label('Number of Vehicles'),
	multiple_trips: Yup.boolean().nullable().label('Multiple Trips'),
	route_id: Yup.number()
		.required('Route is required')
		.integer('Route ID must be an integer')
		.label('Route'),
	week_off_days: Yup.array().of(Yup.string()).nullable().label('Week Off Days'),
	reporting_time: Yup.mixed()
		.required('Reporting time is required')
		.test(
			'reporting-time-required',
			'Reporting time is required',
			(value) => value !== undefined && value !== null
		)
		.label('Reporting Time'),
	vehicle_cost: Yup.number()
		.required('Vehicle cost is required')
		.min(0, 'Vehicle cost must be at least 0')
		.label('Vehicle Cost'),
	loader_cost: Yup.number()
		.required('Loader cost is required')
		.min(0, 'Loader cost must be at least 0')
		.label('Loader Cost'),
	delivery_associate_cost: Yup.number()
		.required('Delivery associate cost is required')
		.min(0, 'Delivery associate cost must be at least 0')
		.label('Delivery Associate Cost'),
	threshold_km_per_day: Yup.number().when('billing_type', {
		is: (val) =>
			val === BILLING_TYPE_FIXED ||
			val === BILLING_TYPE_FIXED_VARIABLE ||
			val === BILLING_TYPE_FIXED_PER_DAY,
		then: (schema) =>
			schema
				.required('Threshold km per day is required for Fixed and Fixed + Variable')
				.integer()
				.min(0),
		otherwise: (schema) => schema.nullable(),
	}),
	threshold_km_per_month: Yup.number()
		.when('billing_type', {
			is: (val) =>
				val === BILLING_TYPE_FIXED ||
				val === BILLING_TYPE_FIXED_VARIABLE ||
				val === BILLING_TYPE_FIXED_PER_DAY,
			then: (schema) =>
				schema
					.required('Threshold km per month is required for Fixed and Fixed + Variable')
					.integer()
					.min(0),
			otherwise: (schema) => schema.nullable(),
		})
		// eslint-disable-next-line func-names
		.test(
			'is-greater',
			'Threshold km per month must be greater than threshold km per day',
			function (value) {
				const { threshold_km_per_day } = this.parent;
				if (!value || !threshold_km_per_day) return true;
				return Number(value) > Number(threshold_km_per_day);
			}
		)
		.label('Threshold KM Per Month'),
	fixed_hours_per_day: Yup.number()
		.when('billing_type', {
			is: (val) =>
				val === BILLING_TYPE_FIXED ||
				val === BILLING_TYPE_FIXED_VARIABLE ||
				val === BILLING_TYPE_FIXED_PER_DAY,
			then: (schema) =>
				schema.required('Fixed rate is required for Fixed and Fixed + Variable'),
			otherwise: (schema) => schema.nullable(),
		})
		.label('Fixed rate'),
	extra_charges_per_km: Yup.number()
		.when('billing_type', {
			is: BILLING_TYPE_VARIABLE,
			then: (schema) =>
				schema
					.required('Extra charges per km is required')
					.min(0.01, 'Extra charges per km must be at least 0.01'),
			otherwise: (schema) =>
				schema.when('billing_subtype', {
					// Fixed + Variable
					is: SUB_BILLING_PER_ORDER,
					then: (s) => s.nullable(),
					otherwise: (s) =>
						s
							.required('Extra charges per km is required')
							.min(0, 'Extra charges per km must be at least 0'),
				}),
		})
		.label('Extra Charges Per KM'),
	// Sub Billing Type — required for Variable / Fixed + Variable
	billing_subtype: Yup.string()
		.when('billing_type', {
			is: (val) =>
				val === BILLING_TYPE_VARIABLE ||
				val === BILLING_TYPE_FIXED_VARIABLE ||
				val === BILLING_TYPE_FIXED_PER_DAY,
			then: (schema) => schema.required('Sub billing type is required'),
			otherwise: (schema) => schema.nullable(),
		})
		.label('Sub Billing Type'),
	// Per Order rate lines
	per_order_rates: Yup.array()
		.when('billing_subtype', {
			is: SUB_BILLING_PER_ORDER,
			then: (schema) =>
				schema
					.min(PER_ORDER_MIN_ROWS, `At least ${PER_ORDER_MIN_ROWS} rates are required`)
					.max(PER_ORDER_MAX_ROWS, `At most ${PER_ORDER_MAX_ROWS} rates are allowed`)
					.of(
						Yup.object({
							rate_id: Yup.string().required('Rate ID is required'),
							rate: Yup.number()
								.typeError('Rate must be a number')
								.required('Rate is required')
								.moreThan(0, 'Rate must be greater than 0')
								.test(
									'less-than-client-rate',
									'Vendor rate must be less than the client rate',
									function (value) {
										const clientRate = this.parent?.client_rate;
										if (
											value === undefined ||
											value === null ||
											clientRate === undefined ||
											clientRate === null ||
											clientRate === ''
										) {
											return true;
										}
										return Number(value) < Number(clientRate);
									}
								),
						})
					),
			otherwise: (schema) => schema.nullable(),
		})
		.label('Order Rates'),
	extra_charges_per_hour: Yup.number()
		.when('billing_type', {
			is: BILLING_TYPE_FIXED_VARIABLE,
			then: (schema) =>
				schema
					.required('Extra charges per hour is required')
					.integer()
					.min(0, 'Extra charges per hour must be at least 0'),
			otherwise: (schema) => schema.nullable(),
		})
		.label('Extra Charges Per Hour'),
	fixed_rate_per_day: Yup.number()
		.nullable()
		.when('billing_type', {
			is: BILLING_TYPE_FIXED_PER_DAY,
			then: (schema) =>
				schema
					.required('Fixed rate per day is required')
					.moreThan(0, 'Fixed rate per day must be greater than 0')
					.test(
						'below-client-daily-rate',
						'Vendor fixed rate per day must be less than the client rate',
						// eslint-disable-next-line func-names
						function (value) {
							const clientRate = this.parent.client_fixed_rate_per_day;
							if (value == null || clientRate == null || clientRate === '')
								return true;
							return Number(value) < Number(clientRate);
						}
					),
			otherwise: (schema) => schema.nullable(),
		})
		.label('Fixed Rate Per Day'),
	other_expenses: Yup.string().nullable().label('Other Expenses'),
	other_other_expenses: Yup.number()
		.nullable()
		.transform((value, originalValue) =>
			typeof originalValue === 'string' && originalValue.trim() === '' ? null : value
		)
		.when('other_expenses', {
			is: (val) => val !== null && val !== undefined && String(val).trim() !== '',
			then: (schema) =>
				schema
					.required('Other expenses amount is required when other expenses is specified')
					.integer()
					.min(0),
			otherwise: (schema) => schema.nullable(),
		})
		.label('Enter Other Expenses'),
});
